/* eslint-disable @typescript-eslint/no-floating-promises */
import i18next, { i18n } from 'i18next';
import LanguageDetector, { DetectorOptions } from 'i18next-browser-languagedetector';
import HttpApi, { BackendOptions } from 'i18next-http-backend';
import { useEffect, useState } from 'react';

// HTTP Path format to retrieve localization files.
const httpBackendOptions: BackendOptions = {
  loadPath: process.env.PUBLIC_URL + '/localization/{{lng}}/{{ns}}.json',
};

// List of localization files to load.
const namespaces = ['misc', 'loading', 'enter-crd', 'email-sent', 'contact-support', 'built-not-activated'];

const detectionOptions: DetectorOptions = {
  order: ['querystring', 'localStorage', 'navigator'],
  lookupQuerystring: 'lng',
  lookupLocalStorage: 'regLng',

  // Caching
  caches: ['localStorage'],
  excludeCacheFor: ['cimode'],
};

/**
 * @returns i18next : A i18n type to be used for localization
 * @return newLang : A function that allows changing the language
 */
const useLocalization = (): [{ i18next: i18n }, (newLang: string) => void] => {
  const [localization, setLocalization] = useState({ i18next });

  // Initialize localization when hook is called.
  useEffect(() => {
    if (!localization.i18next.isInitialized) {
      localization.i18next
        .use(LanguageDetector)
        .use(HttpApi)
        .init({
          detection: detectionOptions,
          backend: httpBackendOptions,
          ns: namespaces,
          supportedLngs: ['en'],
          load: 'currentOnly',
        })
        .then(() => {
          // Set localization as new object to trigger re-renders on initization.
          document.documentElement.lang = localization.i18next.resolvedLanguage;
          setLocalization({ i18next });
        });
    }
  }, [localization]);

  // This will change the language and trigger a state change after new lang files are loaded.
  const changeLanguage = (newLang: string): void => {
    localization.i18next.changeLanguage(newLang, () => {
      document.documentElement.lang = localization.i18next.resolvedLanguage;
      setLocalization({ i18next });
    });
  };

  return [localization, changeLanguage];
};

export default useLocalization;
